// The incoming-call prompt: who's calling, and Accept / Decline. Shown app-wide by
// CallProvider, so it appears over any page when a call rings.
import React, { useState } from "react";
import { Avatar, Box, Dialog, IconButton, Stack, Tooltip, Typography, keyframes } from "@mui/material";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import CallEndRoundedIcon from "@mui/icons-material/CallEndRounded";
import VideocamRoundedIcon from "@mui/icons-material/VideocamRounded";
import CloseFullscreenRoundedIcon from "@mui/icons-material/CloseFullscreenRounded";
import OpenInFullRoundedIcon from "@mui/icons-material/OpenInFullRounded";
import type { CallRecord } from "../../../types/call";
import { initials, avatarColor } from "../../../pages/chat/chatHelpers";

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(60,119,149,0.45); }
  70% { box-shadow: 0 0 0 18px rgba(60,119,149,0); }
  100% { box-shadow: 0 0 0 0 rgba(60,119,149,0); }
`;

interface Props {
  call: CallRecord;
  onAccept: () => void;
  onDecline: () => void;
}

const IncomingCallModal: React.FC<Props> = ({ call, onAccept, onDecline }) => {
  const name = call.initiatorName || "Unknown";
  const isVideo = call.callType === "video";
  // Shrink the ringing prompt to a floating banner so the app stays usable while it
  // keeps ringing.
  const [minimized, setMinimized] = useState(false);

  // ---- Minimised: a small floating banner, still ringing, accept/decline handy ----
  if (minimized) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          position: "fixed",
          bottom: 20,
          right: 20,
          zIndex: 2100,
          px: 1.5,
          py: 1,
          borderRadius: 3,
          bgcolor: "background.paper",
          boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
          border: (t) => `1px solid ${t.palette.divider}`,
          minWidth: 240,
        }}
      >
        <Avatar sx={{ width: 36, height: 36, fontSize: 15, bgcolor: avatarColor(name) }}>
          {initials(name)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>
            {name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Incoming {isVideo ? "video" : "voice"} call
          </Typography>
        </Box>
        <IconButton size="small" onClick={onDecline} sx={{ bgcolor: "#e5484d", color: "#fff", "&:hover": { bgcolor: "#c93b40" } }}>
          <CallEndRoundedIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={onAccept} sx={{ bgcolor: "#2ea043", color: "#fff", "&:hover": { bgcolor: "#278a3a" } }}>
          {isVideo ? <VideocamRoundedIcon fontSize="small" /> : <CallRoundedIcon fontSize="small" />}
        </IconButton>
        <Tooltip title="Expand">
          <IconButton size="small" onClick={() => setMinimized(false)}>
            <OpenInFullRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  }

  return (
    <Dialog open PaperProps={{ sx: { borderRadius: 4, p: 1, minWidth: 320 } }}>
      <Tooltip title="Minimise">
        <IconButton
          onClick={() => setMinimized(true)}
          sx={{ position: "absolute", right: 8, top: 8, color: "text.secondary" }}
        >
          <CloseFullscreenRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Stack alignItems="center" spacing={2} sx={{ px: 3, py: 3 }}>
        <Avatar
          sx={{
            width: 88,
            height: 88,
            fontSize: 34,
            bgcolor: avatarColor(name),
            animation: `${pulse} 1.6s infinite`,
          }}
        >
          {initials(name)}
        </Avatar>
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Incoming {isVideo ? "video" : "voice"} call
            {call.scope === "group" ? " · Group" : ""}
          </Typography>
        </Box>

        <Stack direction="row" spacing={5} sx={{ pt: 1 }}>
          <Stack alignItems="center" spacing={0.5}>
            <IconButton
              onClick={onDecline}
              sx={{ width: 60, height: 60, bgcolor: "#e5484d", color: "#fff", "&:hover": { bgcolor: "#c93b40" } }}
            >
              <CallEndRoundedIcon />
            </IconButton>
            <Typography variant="caption" color="text.secondary">
              Decline
            </Typography>
          </Stack>
          <Stack alignItems="center" spacing={0.5}>
            <IconButton
              onClick={onAccept}
              sx={{ width: 60, height: 60, bgcolor: "#2ea043", color: "#fff", "&:hover": { bgcolor: "#278a3a" } }}
            >
              {isVideo ? <VideocamRoundedIcon /> : <CallRoundedIcon />}
            </IconButton>
            <Typography variant="caption" color="text.secondary">
              Accept
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Dialog>
  );
};

export default IncomingCallModal;
